import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { BulkInvitationResultRow } from "legal-entities/invitations/bulk/_api/bulk-invitation-api-service";

export type BulkInvitationResultsProps = {
  readonly results: readonly BulkInvitationResultRow[];
  readonly noun: string;
};

function plural(count: number, noun: string): string {
  return count === 1 ? `1 ${noun}` : `${count} ${noun}s`;
}

/**
 * What the last send did, row by row.
 *
 * Deliberately not a snackbar. A roster of thirty with two bad addresses invites twenty-eight and
 * explains the two, and "2 failed" that disappears after four seconds leaves somebody to work out
 * which two - which is the whole thing this screen exists to spare them. The rejected rows are
 * still in the grid above with their reasons; this says what became of the rest.
 */
export default function BulkInvitationResults(props: Readonly<BulkInvitationResultsProps>) {
  const invited = props.results.filter((result) => result.outcome === "invited");
  const rejected = props.results.filter((result) => result.outcome === "rejected");
  const noted = invited.filter((result) => Boolean(result.note));

  if (rejected.length === 0) {
    return (
      <Alert severity="success">
        <AlertTitle>{`${plural(invited.length, props.noun)} invited`}</AlertTitle>
        {noted.length === 0 ? null : (
          <List dense disablePadding>
            {noted.map((result) => (
              <ListItem key={result.email} disableGutters disablePadding>
                <ListItemText primary={result.email} secondary={result.note} />
              </ListItem>
            ))}
          </List>
        )}
      </Alert>
    );
  }

  return (
    <Alert severity="warning">
      <AlertTitle>
        {invited.length === 0
          ? `Nothing was sent: ${plural(rejected.length, "row")} needs fixing`
          : `${plural(invited.length, props.noun)} invited, ${plural(rejected.length, "row")} left to fix`}
      </AlertTitle>
      <Typography variant="body2">
        {invited.length === 0
          ? "The rows are still below."
          : "The invited rows have been cleared. What is left below is what still needs attention."}
      </Typography>
      <List dense disablePadding>
        {rejected.map((result) => (
          <ListItem key={`${result.index}-${result.email}`} disableGutters disablePadding>
            <ListItemText primary={result.email || `Row ${result.index + 1}`} secondary={result.error} />
          </ListItem>
        ))}
      </List>
      {noted.length === 0 ? null : (
        <List dense disablePadding>
          {noted.map((result) => (
            <ListItem key={`noted-${result.email}`} disableGutters disablePadding>
              <ListItemText primary={result.email} secondary={result.note} />
            </ListItem>
          ))}
        </List>
      )}
    </Alert>
  );
}
